import { useMutation, useQueryClient } from '@tanstack/react-query';

import { integrationsControllerMarkTaskAsCompleteByProjectCompletionId } from '@/api/core-api';
import { ERROR_CAUSES } from '@/utils/constants';
import { getClient } from '@/utils/get-api-client';
import { markProjectTaskCompleteLocally } from '@/utils/project-task-complete-storage';

import { queryKeys } from './query-keys';

export function useCompleteProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      projectCompletionId,
      userId,
    }: {
      projectCompletionId: string;
      userId: string;
    }) => {
      const client = getClient(userId);
      const response =
        await integrationsControllerMarkTaskAsCompleteByProjectCompletionId({
          path: {
            projectCompletionId,
          },
          client,
        });

      if (response.status === 409) {
        throw new Error('Project already completed', {
          cause: ERROR_CAUSES.PROJECT_ALREADY_COMPLETED,
        });
      }

      if (response.error) {
        throw new Error('Failed to complete project');
      }

      return response.data;
    },
    onSuccess: (data, { userId }) => {
      if (!data?.projectId) {
        return;
      }

      // Survives full document navigations after returning from the survey.
      markProjectTaskCompleteLocally(data.projectId);

      queryClient.removeQueries({
        queryKey: queryKeys.projects.detailBase(data.projectId),
      });

      void queryClient.invalidateQueries({
        queryKey: queryKeys.activeSurveys.byUserId(userId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.appliedSurveys.byUserId(userId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.pastSurveys.byUserId(userId),
      });
    },
  });
}
